import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import { buildDsarQueueView, type DsarQueueView } from './dsarQueueModel';
import type { DsarRequest, Market, User } from '../dataProvider/types';

/**
 * The DSAR queue, read through the aggregation seam (`buildDsarQueueView`), NOT computed in the
 * component. For v1 it pulls raw DSAR requests and users from the mock provider and aggregates
 * client-side; the real-data migration replaces the fetch-and-aggregate with a server-side
 * endpoint and the surface does not change. Same seam idiom as useSuspensions / useTicketInbox.
 *
 * OVERSIGHT-GLOBAL: it does NOT read the region switcher. It is global by default; the optional
 * `regionFilter` (the surface's own facet) narrows by the request's market by exact value. `now`
 * is the injected clock the statutory-deadline derivation is measured against.
 */

const ALL = {
  pagination: { page: 1, perPage: 100_000 },
  sort: { field: 'id', order: 'ASC' as const },
  filter: {},
};

export const useDsarQueue = (
  now: number,
  regionFilter: Market | null,
): { view: DsarQueueView | null; isLoading: boolean } => {
  const dataProvider = useDataProvider();

  const { data, isLoading } = useQuery({
    queryKey: ['dsar-queue', now, regionFilter],
    queryFn: async () => {
      const [dsarRequests, users] = await Promise.all([
        dataProvider.getList('dsarRequests', ALL),
        dataProvider.getList('users', ALL),
      ]);
      return buildDsarQueueView(
        {
          dsarRequests: dsarRequests.data as unknown as DsarRequest[],
          users: users.data as unknown as User[],
        },
        { now, regionFilter },
      );
    },
  });

  return { view: data ?? null, isLoading };
};
